import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * TrailingSlashRedirect Component
 * 
 * Enforces trailing slash URLs for SEO consistency.
 * 
 * EXCLUDES from redirect:
 * - /api/* routes
 * - /admin/* routes  
 * - Static assets (files with extensions)
 * - Special files (robots.txt, sitemap.xml, etc.)
 */
export const TrailingSlashRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { pathname, search, hash } = location;

    // === EXCLUSION RULES ===
    
    // 1. Already has trailing slash - no action needed
    if (pathname.endsWith('/')) {
      return;
    }

    // 2. Root path - no trailing slash needed
    if (pathname === '' || pathname === '/') {
      return;
    }

    // 3. API routes - NEVER redirect
    if (pathname.startsWith('/api')) {
      return;
    }

    // 4. Admin routes - NEVER redirect (handled separately)
    if (pathname.startsWith('/admin')) {
      return;
    }

    // 5. B2B routes - keep consistent with admin
    if (pathname.startsWith('/b2b')) {
      return;
    }

    // 6. Static assets and files with extensions - NEVER redirect
    const fileExtensionRegex = /\.[a-z0-9]{2,5}$/i;
    if (fileExtensionRegex.test(pathname)) {
      return;
    }

    // 7. Special files - NEVER redirect
    const specialFiles = [
      '/robots.txt',
      '/sitemap.xml', 
      '/sitemap-index.xml',
      '/manifest.json',
      '/favicon.ico',
      '/logo192.png',
      '/logo512.png'
    ];
    if (specialFiles.includes(pathname)) {
      return;
    }

    // 8. Next.js/React static paths - NEVER redirect
    if (pathname.startsWith('/_next') || pathname.startsWith('/static')) {
      return;
    }

    // === REDIRECT TO TRAILING SLASH ===
    // Only public page routes reach here
    const newPath = `${pathname}/${search}${hash}`;
    
    // Use replace to avoid adding to browser history (acts like 308)
    navigate(newPath, { replace: true });

  }, [location, navigate]);

  return null; // This component renders nothing
};

export default TrailingSlashRedirect;
